import { db, auth } from "../config/firebase";
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInAnonymously,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  increment,
  Timestamp,
} from "firebase/firestore";
import Loader from "../components/shared/loader/loader";
import * as PusherPushNotifications from "@pusher/push-notifications-web";
import Pusher from "pusher-js";
import { io } from "socket.io-client";
import {
  sendPasswordResetEmail,
  fetchSignInMethodsForEmail,
} from "firebase/auth";
import { api } from "../config/api"; // Import the api instance
import { usePreloadCountries } from "../hooks/usePreloadCountries";
import { set } from "firebase/database";

const beamsClient = new PusherPushNotifications.Client({
  instanceId: process.env.REACT_APP_PUSHER_BEAMS_INSTANCE_ID,
});

const pusher = new Pusher(process.env.REACT_APP_PUSHER_KEY, {
  cluster: process.env.REACT_APP_PUSHER_CLUSTER,
});

// Helper to determine the correct Socket.io base URL
// - When running on localhost, prefer a local socket server
// - Otherwise, fall back to the configured env URL or production URL
const getSocketBaseUrl = () => {
  const envUrl = process.env.REACT_APP_SOCKET_URL;

  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    const isLocalhost =
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "::1";

    if (isLocalhost) {
      // If you have a specific local socket URL, set REACT_APP_SOCKET_URL_LOCAL in .env
      // Otherwise this defaults to the backend running on localhost:8800
      return process.env.REACT_APP_SOCKET_URL_LOCAL || "http://localhost:8800";
    }
  }

  // Non-localhost: use env URL if present, otherwise default production URL
  return envUrl || "https://server.oneunionsolutions.com/";
};

// // Create socket but don't auto-connect — we will connect on login and disconnect on logout
// const socket = io("https://server.oneunionsolutions.com/", { autoConnect: false });
// // const socket = io("https://long-modesty-ndss-ous-154adedd.koyeb.app/");

// // const socket = io(process.env.REACT_APP_SOCKET_URL);
// // console.log("socket from env",process.env.REACT_APP_SOCKET_URL);
// // console.log("socket",socket);
const socket = io(getSocketBaseUrl(), { autoConnect: false });
const { createContext, useContext, useEffect, useState } = require("react");

const AuthContext = createContext({});

export const useMyContext = () => useContext(AuthContext);

export const MyProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [passwordChangeModal, setPasswordChangeModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("");
  const [open, setOpen] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [tokenExpiryTime, setTokenExpiryTime] = useState(null); // Track when token will expire
  const [tokenRefreshTimer, setTokenRefreshTimer] = useState(null); // Timer for refreshing
  const [welcomeTourActive, setWelcomeTourActive] = useState(false);
  const [welcomeTourChecked, setWelcomeTourChecked] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [welcomePopupShown, setWelcomePopupShown] = useState(false);
  const [currentLoginSession, setCurrentLoginSession] = useState(null); // Track current login session
  const [isNewLogin, setIsNewLogin] = useState(false); // Track if this is a new login vs page refresh
  const [globalTerms, setGlobalTerms] = useState({});
  const [isNewTerms, setIsNewTerms] = useState(false); // Track if there are new terms to accept

  const optionsArray = [
    "super admin",
    "managing admin",
    "lead admin",
    "editor",
  ];
  const { countries: preloadedCountries, loading: countriesLoading } =
    usePreloadCountries();

  // Constants to define refresh intervals
  const TOKEN_EXPIRY_BUFFER = 10 * 60 * 1000; // Refresh token 10 minutes before expiry
  const TOKEN_DEFAULT_EXPIRY = 55 * 60 * 1000; // Default to 55 minutes (Firebase tokens last 1 hour)
  const TOKEN_REFRESH_MIN_INTERVAL = 5 * 60 * 1000; // Minimum 5 minutes between refresh attempts

  // Helper function to generate a unique session ID
  const generateSessionId = () => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Helper function to check if welcome popup was shown for current session
  const wasWelcomePopupShownThisSession = () => {
    if (!currentLoginSession) return false;
    const sessionData = sessionStorage.getItem("welcomePopupSession");
    return sessionData === currentLoginSession;
  };

  // Helper function to mark welcome popup as shown for current session
  const markWelcomePopupShownThisSession = () => {
    if (currentLoginSession) {
      sessionStorage.setItem("welcomePopupSession", currentLoginSession);
      setWelcomePopupShown(true);
    }
  };

  // Helper function to determine if this is a new login session
  const checkIfNewLoginSession = () => {
    const storedSession = sessionStorage.getItem("currentLoginSession");
    const authStateChanged = sessionStorage.getItem("authStateChanged");

    if (!storedSession) {
      // No stored session means this is a new login
      return true;
    }

    // Check if auth state changed (indicating a new login)
    if (authStateChanged === "true") {
      sessionStorage.removeItem("authStateChanged");
      return true;
    }

    return false;
  };

  // **THE FIX IS HERE**
  // This new interceptor setup is more reliable. It runs only once and
  // fetches the latest token directly from Firebase Auth on every API call.
  // This completely eliminates the timing issue seen after login.
  useEffect(() => {
    const interceptor = api.interceptors.request.use(
      async (config) => {
        // Do NOT override Authorization for guest external-link calls (/external-links/{linkId}/...)
        // Admin endpoints live under /external-links/admin/... and should still receive Firebase tokens
        const url = config.url || "";
        const isAdminExternalEndpoint = url.includes("/external-links/admin/");
        const looksLikeGuestExternal = /\/external-links\/[^/]+\//.test(url); // has a linkId segment
        if (!isAdminExternalEndpoint && looksLikeGuestExternal) {
          return config;
        }

        const currentUser = auth.currentUser;
        if (currentUser) {
          const freshToken = await currentUser.getIdToken();
          // Only set if not already provided by caller
          if (!config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${freshToken}`;
          }
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Clean up the interceptor when the app is closed.
    return () => {
      api.interceptors.request.eject(interceptor);
    };
  }, []); // The empty dependency array [] ensures this effect runs only once.

  // Function to decode token and get expiry time
  const getTokenExpiryTime = (token) => {
    if (!token) return null;

    try {
      // Decode the JWT payload
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const payload = JSON.parse(window.atob(base64));

      // Get expiry timestamp (exp) from token payload
      if (payload.exp) {
        return payload.exp * 1000; // Convert to milliseconds
      }

      // Fallback if no exp in token
      return Date.now() + TOKEN_DEFAULT_EXPIRY;
    } catch (error) {
      console.error("Error decoding token:", error);
      return Date.now() + TOKEN_DEFAULT_EXPIRY;
    }
  };

  // Core token refresh function
  const refreshToken = async (force = false) => {
    // Only refresh if user is authenticated
    if (!isAuthenticated || !auth.currentUser) return null;

    // Check current time and token expiry
    const currentTime = Date.now();

    // Skip refresh if not forced and token was refreshed recently
    const lastRefreshTime =
      currentTime - (tokenExpiryTime - TOKEN_DEFAULT_EXPIRY);
    if (!force && lastRefreshTime < TOKEN_REFRESH_MIN_INTERVAL) {
      return token;
    }

    try {
      const newToken = await auth.currentUser.getIdToken(force);
      const newExpiryTime = getTokenExpiryTime(newToken);

      setToken(newToken);
      setTokenExpiryTime(newExpiryTime);

      // Schedule next refresh based on new expiry time
      scheduleTokenRefresh(newExpiryTime);

      return newToken;
    } catch (error) {
      console.error("Error refreshing token:", error);
      if (error.code === "auth/requires-recent-login") {
        // For security critical operations, consider forcing re-login
        logout();
      }
      return null;
    }
  };

  // Check token expiration and refresh if needed
  const checkTokenExpiration = async () => {
    if (!token || !tokenExpiryTime) return;

    const currentTime = Date.now();
    const timeToExpiry = tokenExpiryTime - currentTime;

    // If token is expired or will expire soon, refresh it
    if (timeToExpiry < TOKEN_EXPIRY_BUFFER) {
      await refreshToken(true);
      return true;
    }

    return false;
  };

  // Schedule token refresh based on expiry time
  const scheduleTokenRefresh = (expiryTime) => {
    // Clear any existing timer
    if (tokenRefreshTimer) {
      clearTimeout(tokenRefreshTimer);
    }

    // Calculate when to refresh (10 minutes before expiry)
    const refreshTime = expiryTime - Date.now() - TOKEN_EXPIRY_BUFFER;

    // Schedule refresh (use minimum 1 minute to avoid immediate refresh)
    const timerDuration = Math.max(refreshTime, 60000);

    const timer = setTimeout(() => {
      refreshToken(true);
    }, timerDuration);

    setTokenRefreshTimer(timer);
  };

  // Configure axios when auth state changes
  useEffect(() => {
    // If user is authenticated, initialize token
    if (isAuthenticated && auth.currentUser) {
      // Get initial token
      auth.currentUser.getIdToken().then((newToken) => {
        const expiryTime = getTokenExpiryTime(newToken);
        setToken(newToken);
        setTokenExpiryTime(expiryTime);

        // Schedule refresh
        scheduleTokenRefresh(expiryTime);
      });
    }

    return () => {
      // Clean up timer on unmount
      if (tokenRefreshTimer) {
        clearTimeout(tokenRefreshTimer);
      }
    };
  }, [isAuthenticated]);

  const updateUserProfile = (newProfileData) => {
    setUser((prevUser) => ({ ...prevUser, ...newProfileData }));
  };

  const initializeBeams = (user) => {
    beamsClient
      .start()
      .then((beamsClient) => beamsClient.getDeviceId())
      .then((deviceId) =>
        console.log("Successfully registered with Beams. Device ID:", deviceId),
      )
      .catch(console.error)
      .then(async () => {
        const insterest = optionsArray.includes(user?.role)
          ? "admin-notifications"
          : `notifications-${user?.uid}`;
        await beamsClient.addDeviceInterest(insterest);
      })
      .catch(console.error);
  };

  const checkAuthStatus = () => {
    // This function checks if the user is already authenticated
    const user = auth.currentUser;
    if (user) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
  };

  const forgotPassword = async (email) => {
    try {
      // Delegate to backend so that:
      // - Firestore access happens via Admin SDK (no client-side rules issues)
      // - Password reset email is triggered from a trusted environment
      const response = await api.post("/auth/forgot-password", { email });

      if (response?.data?.ok) {
        return { isSuccess: true };
      }

      return { isSuccess: false, error: response?.data };
    } catch (error) {
      console.error("Forgot password error:", error?.response?.data || error);
      return { isSuccess: false, error };
    }
  };

  // Function to check welcome tour eligibility - only runs once on initial auth
  const checkWelcomeTourEligibility = async (userId) => {
    if (welcomeTourChecked) return; // Prevent multiple checks

    try {
      const userRef = doc(db, "users", userId);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();

      // Only start tour if user is new and role is "user"
      if (userData?.role === "user" && !userData?.welcomeTour) {
        setWelcomeTourActive(true);
      }
      setWelcomeTourChecked(true);
    } catch (error) {
      console.error("Error checking welcome tour eligibility:", error);
      setWelcomeTourChecked(true);
    }
  };

  // Function to complete welcome tour
  const completeWelcomeTour = async (userId) => {
    try {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        welcomeTour: true,
      });
      setWelcomeTourActive(false);
    } catch (error) {
      console.error("Error completing welcome tour:", error);
    }
  };

  // Allow users to manually restart/open the guided tour.
  const startWelcomeTour = () => {
    localStorage.removeItem("welcomeTourCompleted");
    setWelcomeTourChecked(true);
    setWelcomeTourActive(true);
  };

  const getGlobalTerms = async () => {
    try {
      const res = await api.get("/terms-conditions?search=global");
      const { data } = res.data;
      setGlobalTerms(data[0] || {});
    } catch (error) {
      console.error("Error fetching global terms:", error);
    }
  };

  // Function to check if user has accepted terms and conditions
  const checkTermsAcceptance = async (userId) => {
    // Wait for global terms to be loaded
    if (!globalTerms || Object.keys(globalTerms).length === 0) {
      await getGlobalTerms();
      if (!globalTerms || Object.keys(globalTerms).length === 0) return;
    }

    try {
      const userRef = doc(db, "users", userId);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();

      // Don't show terms modal for admin roles
      if (optionsArray.includes(userData?.role)) {
        setTermsAccepted(true); // Set as accepted for admins
        setShowTermsModal(false); // Hide terms modal

        // Show welcome popup only for new logins and if it hasn't been shown this session
        if (isNewLogin && !wasWelcomePopupShownThisSession()) {
          setOpen(true);
        }

        // Only check for password change for admins after terms check
        if (!userData?.passwordChanged) {
          setPasswordChangeModal(true);
        }
        return;
      }

      if (userData.termsVersion !== globalTerms?.version) {
        setTermsAccepted(false);
        setIsNewTerms(true); // Set as new terms for users
        setShowTermsModal(true); // Show terms modal
        setOpen(false); // Hide welcome popup
        setPasswordChangeModal(false); // Make sure password modal is hidden while showing terms
        return;
      }

      // Check if termsAccepted exists and is true
      if (
        userData?.termsAccepted === true &&
        userData?.termsVersion === globalTerms?.version
      ) {
        setTermsAccepted(true);
        setShowTermsModal(false); // Don't show terms modal

        // Show welcome popup only for new logins and if it hasn't been shown this session
        if (isNewLogin && !wasWelcomePopupShownThisSession()) {
          setOpen(true);
        }

        // Only check for password change after confirming terms are accepted
        if (!userData?.passwordChanged) {
          setPasswordChangeModal(true);
        }
      } else {
        // If termsAccepted doesn't exist or is false
        setTermsAccepted(false);

        setShowTermsModal(true); // Show terms modal
        setOpen(false); // Hide welcome popup
        setPasswordChangeModal(false); // Make sure password modal is hidden while showing terms
      }
    } catch (error) {
      console.error("Error checking terms acceptance:", error);
      // Default to showing terms modal in case of error
      setTermsAccepted(false);
      setShowTermsModal(true);
      setPasswordChangeModal(false); // Make sure password modal is hidden
    }
  };

  // Function to update terms acceptance in Firestore
  const updateTermsAcceptance = async (userId) => {
    try {
      const userRef = doc(db, "users", userId);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();

      await updateDoc(userRef, {
        termsAccepted: true,
        termsVersion: globalTerms?.version,
      });
      setTermsAccepted(true);
      setIsNewTerms(false); // Reset the new terms flag
      setShowTermsModal(false); // Hide terms modal after acceptance

      // Show welcome popup only for new logins and if it hasn't been shown this session
      if (isNewLogin && !wasWelcomePopupShownThisSession()) {
        setOpen(true);
      }

      // Check if password needs to be changed after terms are accepted
      if (!userData?.passwordChanged) {
        setPasswordChangeModal(true);
      }
    } catch (error) {
      console.error("Error updating terms acceptance:", error);
    }
  };

  // Function to check if account is locked
  const isAccountLocked = (userData) => {
    // Only check for user role
    if (userData.role !== "user") return false;

    // If account is explicitly locked
    if (userData.accountLocked === true) {
      // Check if last attempt was today
      if (userData.lastLoginAttempt) {
        const lastAttemptDate = userData.lastLoginAttempt.toDate();
        const today = new Date();

        // If last attempt was not today, unlock the account
        if (
          lastAttemptDate.getDate() !== today.getDate() ||
          lastAttemptDate.getMonth() !== today.getMonth() ||
          lastAttemptDate.getFullYear() !== today.getFullYear()
        ) {
          return false;
        }
        return true;
      }
    }
    return false;
  };

  // Function to handle failed login attempt
  const handleFailedLoginAttempt = async (userId, userData) => {
    try {
      // Only track attempts for user role
      if (userData.role !== "user") return null;

      const userRef = doc(db, "users", userId);
      const today = new Date();
      let failedAttempts = userData.failedLoginAttempts || 0;

      // Check if last attempt was today
      if (userData.lastLoginAttempt) {
        const lastAttemptDate = userData.lastLoginAttempt.toDate();

        // If last attempt was not today, reset counter
        if (
          lastAttemptDate.getDate() !== today.getDate() ||
          lastAttemptDate.getMonth() !== today.getMonth() ||
          lastAttemptDate.getFullYear() !== today.getFullYear()
        ) {
          failedAttempts = 0;
        }
      }

      // Increment failed attempts
      failedAttempts++;

      // Update user record
      const updates = {
        failedLoginAttempts: failedAttempts,
        lastLoginAttempt: Timestamp.now(),
      };

      // Lock account if 5 or more failed attempts
      if (failedAttempts >= 5) {
        updates.accountLocked = true;
      }

      await updateDoc(userRef, updates);

      // Return remaining attempts (out of 5)
      return 5 - failedAttempts;
    } catch (error) {
      console.error("Error handling failed login attempt:", error);
      return null;
    }
  };

  // Function to reset login attempts on successful login
  const resetLoginAttempts = async (userId) => {
    try {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        failedLoginAttempts: 0,
        accountLocked: false,
      });
    } catch (error) {
      console.error("Error resetting login attempts:", error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Firestore rules often require `request.auth != null`. For guest/public pages we
        // bootstrap anonymous auth, but we do NOT treat it as an application login.
        if (user.isAnonymous) {
          setUser(null);
          setIsAuthenticated(false);
          setRole("");
          setToken(null);
          setTokenExpiryTime(null);
          setLoading(false);
          return;
        }

        const ref = doc(db, "users", user.uid);
        const res = await getDoc(ref);
        setUser({
          uid: user.uid,
          ...res.data(),
        });

        // Get initial token and set token expiry
        const initialToken = await user.getIdToken();
        const expiryTime = getTokenExpiryTime(initialToken);
        setToken(initialToken);
        setTokenExpiryTime(expiryTime);

        // Schedule token refresh
        scheduleTokenRefresh(expiryTime);

        // Determine if this is a new login or page refresh
        const newLoginSession = checkIfNewLoginSession();
        setIsNewLogin(newLoginSession);

        // Handle session management
        const storedSession = sessionStorage.getItem("currentLoginSession");
        if (!storedSession || newLoginSession) {
          // This is a new login session
          const newSessionId = generateSessionId();
          setCurrentLoginSession(newSessionId);
          sessionStorage.setItem("currentLoginSession", newSessionId);
          setWelcomePopupShown(false); // Reset for new session
        } else {
          // This is a page refresh or existing session
          setCurrentLoginSession(storedSession);
          setWelcomePopupShown(wasWelcomePopupShownThisSession());
        }

        // Handle authentication flow - first check terms, then handle password change
        await checkTermsAcceptance(user.uid);

        // Only check welcome tour eligibility on initial auth
        if (!welcomeTourChecked) {
          await checkWelcomeTourEligibility(user.uid);
        }

        checkInactivity();
        setIsAuthenticated(true);

        // Subscribe pusher channel to receive real time notifications
    
        const isAdmin = optionsArray.includes(res?.data()?.role);

        const subscriptions = isAdmin
          ? [
              {
                channelName: "admin-notifications-channel",
                eventName: "new-notification",
              },
              {
                channelName: "admin-notifications",
                eventName: "admin-notifications",
              },
            ]
          : [
              {
                channelName: `notifications-${user?.uid}`,
                eventName: "user-notifications",
              },
            ];

        subscriptions.forEach(({ channelName, eventName }) => {
          const channel = pusher.subscribe(channelName);
          channel.bind(eventName, function (newNotification) {
            setNotifications((prev) => [newNotification, ...prev]);
          });
        });

        if (!socket.connected) {
          socket.connect();
        }

        socket.off("ticket-listner");
        socket.off("status-listner");

       socket.on("ticket-listner", (message) => {
  // ❌ Ignore notification-type messages
  if (message?.type === "external-estimate-created") return;

  setMessages((messages) => [...messages, message]);
});

        socket.on("status-listner", (onlineUsers) => {
          setOnlineUsers(onlineUsers);
        });

        //emit status for this socket session
        socket.emit("status", {
          userId: user?.uid,
          status: true,
          role: res?.data()?.role,
        });

        // socket.on("ticket-listner", (message) => {
        //   setMessages((messages) => [...messages, message]);
        // });

        // socket.emit("status", {
        //   userId: user?.uid,
        //   status: true,
        //   role: res?.data()?.role,
        // });

        // // Listen for online status updates
        // socket.on("status-listner", (onlineUsers) => {
        //   setOnlineUsers(onlineUsers);
        // });

        initializeBeams({
          uid: user.uid,
          ...res.data(),
        });
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setWelcomeTourActive(false);
        setWelcomeTourChecked(false);
        setTermsAccepted(false);
        setShowTermsModal(false);
        setPasswordChangeModal(false);
        setWelcomePopupShown(false);
        setCurrentLoginSession(null);
        setIsNewLogin(false);
        setToken(null);
        setTokenExpiryTime(null);

        // Clear session storage
        sessionStorage.removeItem("currentLoginSession");
        sessionStorage.removeItem("welcomePopupSession");
        sessionStorage.removeItem("authStateChanged");

        // Clear token refresh timer if it exists
        if (tokenRefreshTimer) {
          clearTimeout(tokenRefreshTimer);
          setTokenRefreshTimer(null);
        }

        const skipAnon = sessionStorage.getItem("skipAnonBootstrap") === "true";
        if (skipAnon) {
          // This sign-out came from the 2FA login flow; do NOT trigger
          // anonymous auth or the global loading spinner. Just clear the flag
          // and let the app render (e.g. OTP verification screen).
          sessionStorage.removeItem("skipAnonBootstrap");
          setLoading(false);
          return;
        }

        // If Firestore is locked down to authenticated users, guest flows will fail
        // unless we ensure an authenticated Firebase user exists. Anonymous auth
        // satisfies `request.auth != null` without changing the app's login UX.
        try {
          setLoading(true);
          await signInAnonymously(auth);
          return; // onAuthStateChanged will fire again with the anonymous user
        } catch (error) {
          // If anonymous auth isn't enabled in Firebase Console, continue as logged-out.
          console.warn("Anonymous auth bootstrap failed:", error);
        }
      }
      setLoading(false);
    });

    checkAuthStatus();

    return () => {
      if (user?.uid) {
        const isAdmin = optionsArray.includes(user?.role);
        if (isAdmin) {
          pusher.unsubscribe("admin-notifications-channel");
          pusher.unsubscribe("admin-notifications");
        } else {
          pusher.unsubscribe(`notifications-${user?.uid}`);
        }
      }

      unsubscribe();
      // Clear token refresh timer on unmount
      if (tokenRefreshTimer) {
        clearTimeout(tokenRefreshTimer);
        setTokenRefreshTimer(null);
      }
    };
  }, [welcomeTourChecked]);

  // const login = async (email, password) => {
  //   try {
  //     // Mark that a login is happening (to differentiate from page refresh)
  //     sessionStorage.setItem('authStateChanged', 'true');

  //     // Check if user exists in Firestore
  //     const usersRef = collection(db, "users");
  //     const q = query(usersRef, where("email", "==", email));
  //     const querySnapshot = await getDocs(q);

  //     if (querySnapshot.empty) {
  //       throw { code: "auth/user-not-found" };
  //     }

  //     const userDoc = querySnapshot.docs[0];
  //     const userData = userDoc.data();
  //     const userId = userDoc.id;

  //     // Check if account is locked - only applies to "user" role
  //     if (userData.role === "user" && isAccountLocked(userData)) {
  //       // Check if using tempPassword (bypass lock)
  //       if (userData.tempPassword === password) {
  //         // Allow login with temp password even if account is locked
  //         setUser({
  //           uid: userId,
  //           ...userData
  //         });

  //         // Reset failed login attempts since temp password is correct
  //         await resetLoginAttempts(userId);

  //         // Generate new session ID for successful login
  //         const newSessionId = generateSessionId();
  //         setCurrentLoginSession(newSessionId);
  //         sessionStorage.setItem('currentLoginSession', newSessionId);
  //         setWelcomePopupShown(false);
  //         setIsNewLogin(true);

  //         // Check terms acceptance status
  //         await checkTermsAcceptance(userId);

  //         //Check if 2fa is enabled for user
  //         if (userData?.is2faEnabled) {
  //           return {
  //             data: { ...userData, id: userId },
  //             isSuccess: true,
  //             requires2fa: true
  //           }
  //         }

  //         setIsAuthenticated(true);
  //         return { data: { ...userData, id: userId }, isSuccess: true, requires2fa: false };
  //       }

  //       // Return account locked error
  //       return {
  //         isSuccess: false,
  //         error: {
  //           code: "auth/account-locked",
  //           message: "Your account has been locked due to too many failed login attempts. You can contact admin at operations@oneunionsolutions.com or reset your password."
  //         }
  //       };
  //     }

  //     // Try regular Firebase authentication
  //     try {
  //       const authResult = await signInWithEmailAndPassword(auth, email, password);

  //       // If successful and user role is "user", reset failed attempts
  //       if (userData.role === "user") {
  //         await resetLoginAttempts(userId);
  //       }

  //       // Generate new session ID for successful login
  //       const newSessionId = generateSessionId();
  //       setCurrentLoginSession(newSessionId);
  //       sessionStorage.setItem('currentLoginSession', newSessionId);
  //       setWelcomePopupShown(false);
  //       setIsNewLogin(true);

  //       // Check terms acceptance status
  //       await checkTermsAcceptance(userId);
  //       if (userData?.is2faEnabled) {
  //         return {
  //           data: { ...userData, id: userId },
  //           isSuccess: true,
  //           requires2fa: true
  //         }
  //       }
  //       setIsAuthenticated(true);
  //       return { data: { ...userData, id: userId }, isSuccess: true, requires2fa: false };
  //     } catch (firebaseError) {
  //       // If Firebase auth fails, check for temp password
  //       if (userData.tempPassword === password) {
  //         // Allow login with temp password
  //         setUser({
  //           uid: userId,
  //           ...userData
  //         });

  //         // Reset failed login attempts since temp password is correct
  //         if (userData.role === "user") {
  //           await resetLoginAttempts(userId);
  //         }

  //         // Generate new session ID for successful login
  //         const newSessionId = generateSessionId();
  //         setCurrentLoginSession(newSessionId);
  //         sessionStorage.setItem('currentLoginSession', newSessionId);
  //         setWelcomePopupShown(false);
  //         setIsNewLogin(true);

  //         // Check terms acceptance status
  //         await checkTermsAcceptance(userId);

  //         //Check if 2fa is enabled for user
  //         if (userData?.is2faEnabled) {
  //           return {
  //             data: { ...userData, id: userId },
  //             isSuccess: true,
  //             requires2fa: true
  //           }
  //         }

  //         setIsAuthenticated(true);
  //         return { data: { ...userData, id: userId }, isSuccess: true, requires2fa: false };
  //       }

  //       // Track failed login attempt if role is "user"
  //       if (userData.role === "user") {
  //         const remainingAttempts = await handleFailedLoginAttempt(userId, userData);

  //         // If this was the 5th attempt, return locked error
  //         if (remainingAttempts <= 0) {
  //           return {
  //             isSuccess: false,
  //             error: {
  //               code: "auth/account-locked",
  //               message: "Your account has been locked due to too many failed login attempts. You can contact admin at operations@oneunionsolutions.com or reset your password."
  //             }
  //           };
  //         }

  //         // For all other errors, include the remaining attempts
  //         return {
  //           isSuccess: false,
  //           error: {
  //             ...firebaseError,
  //             code: firebaseError.code || "auth/wrong-password" // Ensure there's always a code
  //           },
  //           remainingAttempts: remainingAttempts
  //         };
  //       }

  //       // Return original firebase error without remaining attempts for non-user roles
  //       return { isSuccess: false, error: firebaseError };
  //     }
  //   } catch (error) {
  //     console.error("Login error:", error);
  //     return { isSuccess: false, error };
  //   }
  // };

  const login = async (email, password) => {
    try {
      sessionStorage.setItem("authStateChanged", "true");
      try {
        let authResult;
        let tempLoginData = null;

        // Pre-check lock status: if locked, only temp password is allowed
        const lockResponse = await api.post("/auth/login-status", { email });
        const lockData = lockResponse?.data;
        if (lockData?.ok && lockData?.accountLocked) {
          const lockedResponse = await api.post("/auth/login", {
            email,
            password,
            mode: "fallback",
          });
          const lockedData = lockedResponse?.data;

          if (!lockedData?.ok) {
            return {
              isSuccess: false,
              error: {
                code: "auth/account-locked",
                message:
                  "Your account has been locked due to too many failed login attempts. You can contact admin at operations@oneunionsolutions.com or reset your password.",
              },
              remainingAttempts: 0,
            };
          }

          tempLoginData = lockedData;

          if (lockedData?.requires2fa) {
            return {
              data: { id: lockedData?.userId, email: lockedData?.email },
              isSuccess: true,
              requires2fa: true,
              credentials: {
                email,
                isTemp: true,
                customToken: lockedData?.customToken,
              },
            };
          }

          authResult = await signInWithCustomToken(
            auth,
            lockedData?.customToken,
          );
        } else {
          try {
            // Primary auth via Firebase client SDK
            authResult = await signInWithEmailAndPassword(
              auth,
              email,
              password,
            );
          } catch (authError) {
            // If Firebase auth fails, check temp password + attempts via backend
            const loginResponse = await api.post("/auth/login", {
              email,
              password,
              mode: "fallback",
            });
            tempLoginData = loginResponse?.data;

            if (!tempLoginData?.ok) {
              if (tempLoginData?.code === "auth/account-locked") {
                return {
                  isSuccess: false,
                  error: {
                    code: "auth/account-locked",
                    message:
                      "Your account has been locked due to too many failed login attempts. You can contact admin at operations@oneunionsolutions.com or reset your password.",
                  },
                  remainingAttempts: 0,
                };
              }

              return {
                isSuccess: false,
                error: { code: tempLoginData?.code || "auth/wrong-password" },
                remainingAttempts: tempLoginData?.remainingAttempts,
              };
            }

            if (tempLoginData?.requires2fa) {
              return {
                data: {
                  id: tempLoginData?.userId,
                  email: tempLoginData?.email,
                },
                isSuccess: true,
                requires2fa: true,
                credentials: {
                  email,
                  isTemp: true,
                  customToken: tempLoginData?.customToken,
                },
              };
            }

            authResult = await signInWithCustomToken(
              auth,
              tempLoginData?.customToken,
            );
          }
        }

        const user = authResult.user;
        const userId = user.uid;

        // 2. Fetch User Data (Now authorized to read own doc)
        const userRef = doc(db, "users", userId);
        const userDoc = await getDoc(userRef);

        if (!userDoc.exists()) {
          await signOut(auth);
          return { isSuccess: false, error: { code: "auth/user-not-found" } };
        }

        const userData = userDoc.data();

        // 3. Check Account Lock (Post-Auth)
        if (userData.role === "user" && isAccountLocked(userData)) {
          await signOut(auth); // Immediately sign out if locked

          return {
            isSuccess: false,
            error: {
              code: "auth/account-locked",
              message:
                "Your account has been locked due to too many failed login attempts. You can contact admin at operations@oneunionsolutions.com or reset your password.",
            },
          };
        }

        // 4. Check 2FA
        if (userData?.is2faEnabled) {
          // Sign out to force OTP flow, similar to original logic.
          // Mark that this is an intentional 2FA transition so we don't
          // trigger the global anonymous-auth loader flicker.
          sessionStorage.setItem("skipAnonBootstrap", "true");
          await signOut(auth);

          // For temp-password / fallback logins we may already have a custom token.
          // For normal password logins we will re-use email/password after OTP.
          return {
            data: { ...userData, id: userId },
            isSuccess: true,
            requires2fa: true,
            credentials: {
              email,
              isTemp: Boolean(tempLoginData?.isTemp),
              customToken: tempLoginData?.customToken || null,
              password, // used to re-auth after OTP when no custom token
            },
          };
        }

        // 5. Success Flow
        if (userData.role === "user") {
          await resetLoginAttempts(userId);
        }

        const newSessionId = generateSessionId();
        setCurrentLoginSession(newSessionId);
        sessionStorage.setItem("currentLoginSession", newSessionId);
        setWelcomePopupShown(false);
        setIsNewLogin(true);

        await checkTermsAcceptance(userId);
        setIsAuthenticated(true);

        return {
          data: { ...userData, id: userId },
          isSuccess: true,
          requires2fa: false,
        };
      } catch (authError) {
        console.error("Authentication failed:", authError);
        return { isSuccess: false, error: authError };
      }
    } catch (error) {
      console.error("Login logic error:", error);
      return { isSuccess: false, error };
    }
  };

  const signUp = async (values) => {
    await createUserWithEmailAndPassword(auth, values.email, values.password)
      .then(async (userCredential) => {
        const user = userCredential.user;
        const userRef = doc(db, "users", user.uid);
        const data = {
          firstName: values?.firstName,
          lastName: values?.lastName,
          phone: values?.phone,
          email: values?.email,
          failedLoginAttempts: 0,
          accountLocked: false,
        };
        await setDoc(userRef, data, { merge: true });
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
      });
  };

  const checkInactivity = () => {
    const lastActivityTimestamp = localStorage.getItem("lastActivityTimestamp");
    if (lastActivityTimestamp) {
      const currentTime = Date.now();
      const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 60 minutes in milliseconds
      if (currentTime - parseInt(lastActivityTimestamp) > INACTIVITY_TIMEOUT) {
        console.log("User inactive for 1 Hour. Logging out.");
        logout();
      }
    }
  };

  const updateActivityTimestamp = () => {
    localStorage.setItem("lastActivityTimestamp", Date.now().toString());
  };

  // Simplified activity tracking
  useEffect(() => {
    if (isAuthenticated) {
      const events = [
        "mousedown",
        "mousemove",
        "keypress",
        "scroll",
        "touchstart",
      ];
      const throttledUpdateActivity = () => {
        const now = Date.now();
        const lastTime = localStorage.getItem("lastActivityTimestamp");
        // Only update timestamp if >1 minute since last update
        if (!lastTime || now - parseInt(lastTime) > 60000) {
          updateActivityTimestamp();
        }
      };

      // Add event listeners for user activity
      events.forEach((event) => {
        document.addEventListener(event, throttledUpdateActivity);
      });

      // Check inactivity periodically
      const inactivityCheckId = setInterval(checkInactivity, 60000);

      return () => {
        events.forEach((event) => {
          document.removeEventListener(event, throttledUpdateActivity);
        });
        clearInterval(inactivityCheckId);
      };
    }
  }, [isAuthenticated]);

  const logout = async () => {
    try {
      try {
        if (socket && socket.connected) {
          socket.emit("logout", user?.uid);
        } else if (socket) {
          socket.connect();
          socket.emit("logout", user?.uid);
        }
      } catch (error) {
        console.error("Socket logout emit failed:", error);
      }

      try {
        socket.off("ticket-listner");
        socket.off("status-chat");
        socket.disconnect();
      } catch (error) {
        console.error("Socket cleanup failed", error);
      }

      await signOut(auth);
      setUser(null);
      setIsAuthenticated(false);
      setToken(null);
      setTokenExpiryTime(null);
      setWelcomePopupShown(false);
      setCurrentLoginSession(null);
      setIsNewLogin(false);
      localStorage.removeItem("lastActivityTimestamp");

      // Clear session storage
      sessionStorage.removeItem("currentLoginSession");
      sessionStorage.removeItem("welcomePopupSession");
      sessionStorage.removeItem("authStateChanged");

      // Clear token refresh timer if it exists
      if (tokenRefreshTimer) {
        clearTimeout(tokenRefreshTimer);
        setTokenRefreshTimer(null);
      }
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  useEffect(() => {
    return () => {
      try {
        socket.off("ticket-listner");
        socket.off("status-listner");
        // Do not forcibly disconnect here unless you want the socket removed when provider unmounts.
        // socket.disconnect();
      } catch (err) {
        console.error("Provider cleanup error:", err);
      }
    };
  }, []);

  useEffect(() => {
    // if (!globalTerms || Object.keys(globalTerms).length === 0 || !user) return
    getGlobalTerms();
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        signUp,
        logout,
        role,
        setRole,
        passwordChangeModal,
        setPasswordChangeModal,
        open,
        setOpen,
        showTermsModal,
        setShowTermsModal,
        notifications,
        setNotifications,
        messages,
        setMessages,
        onlineUsers,
        setOnlineUsers,
        socket,
        isAuthenticated,
        checkAuthStatus,
        token,
        updateUserProfile,
        forgotPassword,
        welcomeTourActive,
        completeWelcomeTour,
        startWelcomeTour,
        setWelcomeTourActive,
        setWelcomeTourChecked,
        termsAccepted,
        updateTermsAcceptance,
        welcomePopupShown,
        setWelcomePopupShown: markWelcomePopupShownThisSession, // Use the session-aware function
        refreshToken, // Expose token refresh function
        preloadedCountries, // Expose preloaded countries
        globalTerms,
        isNewTerms,
        setIsAuthenticated,
        setUser,
      }}
    >
      {loading ? (
        <div>
          <Loader />
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
